import React, { useState, useEffect, useRef } from 'react';
import pencilIcon from './assets/pencil.png';
import '../../css/ResultsData.css';

const styles = {
  zoneDropdownContainer: {
    fontSize: 14,
    position: 'relative',
    userSelect: 'none',
    display: 'flex',
    flexDirection: 'column'
  },
  zoneLabel: {
    fontWeight: 'bold',
    marginRight: 8,
    alignItems: 'center',
    color: '#425158'
  },
  zoneDropdown: {
    fontSize: 14,
    cursor: 'pointer',
    backgroundColor: '#49a7ab',
    color: 'white',
    maxWidth: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  arrowContainer: {
    backgroundColor: '#457c81',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectedZoneStyle: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    boxSizing: 'border-box',
    textOverflow: 'ellipsis',
    maxWidth: '100px',
  },
  arrowSvg: {
    width: '100%',
    height: '100%',
    fill: 'white',
    transition: 'transform 0.2s ease',
  },
  arrowOpen: {
    transform: 'rotate(180deg)',
  },
  dropdownList: {
    maxHeight: 300,
    overflowY: 'auto',
    backgroundColor: '#01b2b7',
    zIndex: 9999,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    padding: '4px 0',
    marginTop: '24px',
    position: 'fixed',
    overflow: 'auto',
    maxHeight: '140px'
  },
  dropdownItem: {
    padding: '6px 10px',
    color: '#425158',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dropdownItemHover: {
    backgroundColor: '#028a90',
  },
  zoneNameText: {
    flexGrow: 1,
    userSelect: 'none',
  },
  zoneOptionInput: {
    width: 120,
    fontSize: 14,
    padding: 4,
    boxSizing: 'border-box',
  },
  editDeleteButtons: {
    display: 'flex',
    gap: 4,
    marginLeft: 8,
  },
  button: {
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: 12,
    lineHeight: 1,
    padding: '0 3px',
    userSelect: 'none',
  },
  addSuggestion: {
    fontStyle: 'italic',
    opacity: 0.8,
  },
  searchInputContainer: {
    padding: '6px 10px',
    borderBottom: '1px solid rgba(255,255,255,0.15)',
  },
  searchInput: {
    width: '100%',
    fontSize: 14,
    padding: 6,
    boxSizing: 'border-box',
    border: 'none',
    borderRadius: 16,
  },
};

const ZoneName1 = ({ li, cleanParsedEstData, setCleanParsedEstData, claimId }) => {
  if (!li) return <div style={styles.zoneDropdownContainer}></div>;

  const key = Object.keys(li)[0];
  const item = li[key];
  const detectedZone = item?.detectedZone;
  const userModifiedZone = item?.userModifiedZone;
  const currentZone = userModifiedZone || detectedZone || 'unknown';

  const detectedZonesObj =
    cleanParsedEstData?.detectedZones && typeof cleanParsedEstData.detectedZones === 'object'
      ? cleanParsedEstData.detectedZones
      : {};

  const userModifiedZonesObj =
    cleanParsedEstData?.userModifiedZones && typeof cleanParsedEstData.userModifiedZones === 'object'
      ? cleanParsedEstData.userModifiedZones
      : {};

  const systemZoneNames = Object.keys(detectedZonesObj);
  const userZoneNames = Object.keys(userModifiedZonesObj);

  const hasUserModifications =
    JSON.stringify(userZoneNames.sort()) !== JSON.stringify(systemZoneNames.sort());
  const effectiveZonesObj = hasUserModifications ? userModifiedZonesObj : detectedZonesObj;

  const [selectedZone, setSelectedZone] = useState(currentZone);
  const [isOpen, setIsOpen] = useState(false);
  const [zonesObj, setZonesObj] = useState(effectiveZonesObj);
  const [editingZones, setEditingZones] = useState({});
  const [hoveredZone, setHoveredZone] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 0 });

  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    setZonesObj(effectiveZonesObj);
  }, [cleanParsedEstData?.userModifiedZones, cleanParsedEstData?.detectedZones]);

  useEffect(() => {
    const newItem = cleanParsedEstData?.lineItems?.find((obj) => obj[key]);
    const updatedZone =
      newItem?.[key]?.userModifiedZone || newItem?.[key]?.detectedZone || 'unknown';
    setSelectedZone(updatedZone);
  }, [cleanParsedEstData, key]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !triggerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setEditingZones({});
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const saveToBackend = async (updatedData) => {
    if (!claimId) {
      console.warn('No claimId to save zone updates');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_ZONE1_UPDATE}/${claimId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cleanParsedEstData: updatedData }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to save zone update');
    } catch (err) {
      console.error('❌ Error saving zone data:', err);
    }
  };

  const handleZoneChange = (newZone) => {
    setSelectedZone(newZone);
    setIsOpen(false);
    setSearchTerm('');
    setEditingZones({});

    const updatedData = { ...cleanParsedEstData };
    for (const obj of updatedData.lineItems || []) {
      if (obj[key]) {
        obj[key].userModifiedZone = newZone;
      }
    }

    if (!updatedData.userModifiedZones) updatedData.userModifiedZones = {};
    if (!updatedData.userModifiedZones[newZone]) {
      updatedData.userModifiedZones[newZone] = true;
    }

    setCleanParsedEstData(updatedData);
    saveToBackend(updatedData);
  };

  const startEditZone = (zone) => {
    setEditingZones((prev) => ({ ...prev, [zone]: zone }));
  };

  const editZoneChange = (zone, val) => {
    setEditingZones((prev) => ({ ...prev, [zone]: val }));
  };

  const saveZoneEdit = (oldZone) => {
    const newZoneValue = (editingZones[oldZone] || '').trim();
    if (!newZoneValue || newZoneValue === oldZone) {
      setEditingZones((prev) => {
        const copy = { ...prev };
        delete copy[oldZone];
        return copy;
      });
      return;
    }

    const updatedZones = { ...zonesObj };
    delete updatedZones[oldZone];
    updatedZones[newZoneValue] = true;
    setZonesObj(updatedZones);

    const updatedData = { ...cleanParsedEstData };
    updatedData.userModifiedZones = updatedZones;

    for (const itemObj of updatedData.lineItems || []) {
      for (const lineKey in itemObj) {
        if (itemObj[lineKey].userModifiedZone === oldZone) {
          itemObj[lineKey].userModifiedZone = newZoneValue;
        }
      }
    }

    setCleanParsedEstData(updatedData);
    saveToBackend(updatedData);

    setEditingZones((prev) => {
      const copy = { ...prev };
      delete copy[oldZone];
      return copy;
    });
  };

  const handleZoneDelete = (zoneToDelete) => {
    const updatedZones = { ...zonesObj };
    delete updatedZones[zoneToDelete];
    setZonesObj(updatedZones);

    const updatedData = { ...cleanParsedEstData };
    updatedData.userModifiedZones = updatedZones;

    for (const itemObj of updatedData.lineItems || []) {
      for (const lineKey in itemObj) {
        if (itemObj[lineKey].userModifiedZone === zoneToDelete) {
          itemObj[lineKey].userModifiedZone = 'unknown';
        }
      }
    }

    setCleanParsedEstData(updatedData);
    saveToBackend(updatedData);
  };

  const handleAddZone = (zoneName) => {
    if (!zoneName) return;
    const trimmed = zoneName.trim();
    if (!trimmed) return;
    if (zonesObj[trimmed]) {
      setSearchTerm('');
      return;
    }
    const updatedZones = { ...zonesObj, [trimmed]: true };
    setZonesObj(updatedZones);

    const updatedData = { ...cleanParsedEstData };
    updatedData.userModifiedZones = updatedZones;

    setSearchTerm('');
    setCleanParsedEstData(updatedData);
    saveToBackend(updatedData);
    setSelectedZone(trimmed);
    setIsOpen(false);
  };

  const filteredZones = Object.keys(zonesObj).filter((z) =>
    z.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exactMatch = Object.keys(zonesObj).some(
    (z) => z.toLowerCase() === searchTerm.trim().toLowerCase()
  );

  const handleDropdownToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownCoords({
        width: 'auto',
      });
    }
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="responsive-flex">
      <label style={styles.zoneLabel}>ZONE:</label>
      <div title={selectedZone} style={styles.zoneDropdownContainer} ref={dropdownRef}>
        <div
          style={styles.zoneDropdown}
          onClick={handleDropdownToggle}
          tabIndex={0}
          ref={triggerRef}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleDropdownToggle();
            } else if (e.key === 'Escape') {
              setIsOpen(false);
            }
          }}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          role="combobox"
        >
          <span style={styles.selectedZoneStyle}>{selectedZone}</span>
          <div style={styles.arrowContainer}>
            <svg
              style={{ ...styles.arrowSvg, ...(isOpen ? styles.arrowOpen : {}) }}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </div>
        </div>

        {isOpen && (
          <div
            style={{
              ...styles.dropdownList,
              position: 'fixed',
              top: dropdownCoords.top,
              left: dropdownCoords.left,
              width: dropdownCoords.width,
            }}
            role="listbox"
            tabIndex={-1}
          >
            <div style={styles.searchInputContainer}>
              <input
                type="text"
                placeholder="Search or add zone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (!exactMatch && searchTerm.trim() !== '') {
                      handleAddZone(searchTerm);
                    }
                  }
                }}
                autoFocus
                style={styles.searchInput}
                aria-label="Search or add zone"
              />
            </div>

            {!exactMatch && searchTerm.trim() !== '' && (
              <div
                style={{ ...styles.dropdownItem, ...styles.addSuggestion }}
                onClick={() => handleAddZone(searchTerm)}
                tabIndex={0}
                role="option"
                aria-selected={false}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleAddZone(searchTerm);
                  }
                }}
              >
                ➕ Add new zone: "{searchTerm.trim()}"
              </div>
            )}

            {filteredZones.map((zone) => {
              const isEditing = editingZones[zone] !== undefined;
              const isSelected = selectedZone === zone;
              return (
                <div
                  key={zone}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={0}
                  onClick={() => !isEditing && handleZoneChange(zone)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !isEditing) {
                      e.preventDefault();
                      handleZoneChange(zone);
                    }
                  }}
                  onMouseEnter={() => setHoveredZone(zone)}
                  onMouseLeave={() => setHoveredZone(null)}
                  style={{
                    ...styles.dropdownItem,
                    ...(hoveredZone === zone ? styles.dropdownItemHover : {}),
                    fontWeight: isSelected ? 'bold' : 'normal',
                  }}
                >
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={editingZones[zone]}
                        onChange={(e) => editZoneChange(zone, e.target.value)}
                        onBlur={() => saveZoneEdit(zone)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            saveZoneEdit(zone);
                          }
                          if (e.key === 'Escape') {
                            setEditingZones((prev) => {
                              const copy = { ...prev };
                              delete copy[zone];
                              return copy;
                            });
                          }
                        }}
                        autoFocus
                        style={styles.zoneOptionInput}
                      />
                      <div style={styles.editDeleteButtons}>
                        <button
                          onClick={() => handleZoneDelete(zone)}
                          style={styles.button}
                          title="Delete zone"
                          aria-label={`Delete zone ${zone}`}
                        >
                          REMOVE
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span
                        style={styles.zoneNameText}
                        title="Click zone name to change the zone designation for the current line item."
                        onClick={() => handleZoneChange(zone)}
                      >
                        {zone}
                      </span>
                      <div style={styles.editDeleteButtons}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditZone(zone);
                          }}
                          style={styles.button}
                          title="Edit zone"
                          aria-label={`Edit zone ${zone}`}
                        >
                          <img src={pencilIcon} alt="Pencil Icon" style={{ height: '16px', width: 'auto' }} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleZoneDelete(zone);
                          }}
                          style={styles.button}
                          title="Delete zone"
                          aria-label={`Delete zone ${zone}`}
                        >
                          REMOVE
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}

            {!Object.keys(zonesObj).includes('unknown') && (
              <div
                role="option"
                tabIndex={0}
                aria-selected={selectedZone === 'unknown'}
                onClick={() => handleZoneChange('unknown')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleZoneChange('unknown');
                  }
                }}
                onMouseEnter={() => setHoveredZone('unknown')}
                onMouseLeave={() => setHoveredZone(null)}
                style={{
                  ...styles.dropdownItem,
                  ...(hoveredZone === 'unknown' ? styles.dropdownItemHover : {}),
                  fontWeight: selectedZone === 'unknown' ? 'bold' : 'normal',
                }}
              >
                <span style={styles.zoneNameText}>unknown</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ZoneName1;
